import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, config } from "../lib/api.js";
import { PageHeader } from "../components/PageHeader.jsx";
import { EmptyState } from "../components/EmptyState.jsx";
import { Copy } from "../components/icons.jsx";

export function Draft() {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("auto");
  const [lengthHint, setLengthHint] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  const recs = useQuery({
    queryKey: ["recs"],
    queryFn: api.recommendationsRecent,
    enabled: config.hasBase
  });

  if (!config.hasBase) return <EmptyState title="Backend not configured" description="Configure VITE_API_BASE / VITE_API_TOKEN." />;

  async function generate() {
    if (!topic.trim()) return;
    setBusy(true); setError(null); setDraft(null);
    try {
      const r = await api.draftPost({ topic: topic.trim(), tone, lengthHint });
      setDraft(r);
    } catch (e) {
      setError(e.body?.message || e.message || "Failed to generate.");
    } finally {
      setBusy(false);
    }
  }

  async function newRecs() {
    setBusy(true);
    try { await api.recommendations(); recs.refetch(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function copy() {
    if (!draft) return;
    const text = `${draft.draft}\n\n${(draft.hashtags || []).map((h) => `#${h}`).join(" ")}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  }

  const recent = recs.data?.ideas?.[0]?.draft?.recommendations || [];

  return (
    <>
      <PageHeader title="AI Draft" subtitle="Drafts in your voice, grounded in your past posts." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2">
          <label className="label">Topic</label>
          <input className="input mb-3" placeholder="e.g. lessons from a botched product launch"
            value={topic} onChange={(e) => setTopic(e.target.value)} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Tone</label>
              <select className="input" value={tone} onChange={(e) => setTone(e.target.value)}>
                {["auto", "casual", "professional", "story", "contrarian", "educational"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Length</label>
              <select className="input" value={lengthHint} onChange={(e) => setLengthHint(e.target.value)}>
                <option value="short">short</option>
                <option value="medium">medium</option>
                <option value="long">long</option>
              </select>
            </div>
          </div>

          <button className="btn btn-primary mt-4" onClick={generate} disabled={busy || !topic.trim()}>
            {busy ? "Generating…" : "Generate draft"}
          </button>
          {error && <div className="text-rose-400 text-sm mt-3">{error}</div>}

          {draft && (
            <div className="mt-6 border-t border-white/5 pt-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm">
                  Voice match score: <span className="font-semibold">{(draft.voiceMatchScore * 100).toFixed(1)}%</span>
                </div>
                <button className="btn btn-ghost text-xs" onClick={copy}><Copy className="w-3 h-3 mr-1" /> Copy</button>
              </div>
              <textarea className="input min-h-[18rem]" value={draft.draft} onChange={(e) => setDraft({ ...draft, draft: e.target.value })} />
              <div className="mt-3 flex flex-wrap gap-1">
                {draft.hashtags?.map((h) => (
                  <span key={h} className="px-2 py-1 rounded bg-white/5 text-xs">#{h}</span>
                ))}
              </div>
              {draft.inspirationPostUrns?.length > 0 && (
                <div className="text-xs subtle mt-3">
                  Inspired by your posts: {draft.inspirationPostUrns.slice(0, 3).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium">Recommended angles</div>
            <button className="btn btn-ghost text-xs" onClick={newRecs} disabled={busy}>Refresh</button>
          </div>
          {recs.isLoading ? <div className="subtle">Loading…</div> :
            recent.length === 0 ? <div className="subtle text-sm">Click Refresh to generate.</div> : (
              <ul className="space-y-3">
                {recent.map((r, i) => (
                  <li key={i} className="text-sm border-b border-white/5 pb-2">
                    <button className="font-medium hover:underline text-left" onClick={() => setTopic(r.angle)}>{r.angle}</button>
                    <div className="subtle text-xs mt-1">{r.rationale}</div>
                    {r.trendingHashtag && <div className="text-emerald-400 text-xs mt-1">#{r.trendingHashtag}</div>}
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </>
  );
}
